import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError, fileUrl, uploadFile } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Image as ImageIcon, Type, Star, Trash2, UploadCloud, Loader2 } from "lucide-react";

const MAX_IMG = 10 * 1024 * 1024;
const BG_COLORS = ["#0F1A2B", "#3E7C6A", "#7A5C3B", "#A64F5C", "#4B4B7A", "#2E2E2E"];

function isActive(s) {
  if (!s.expires_at) return false;
  try { return new Date(s.expires_at).getTime() > Date.now(); } catch { return false; }
}

export default function StoriesTab() {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("image");
  const [mediaUrl, setMediaUrl] = useState(null);
  const [text, setText] = useState("");
  const [bg, setBg] = useState(BG_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pinTarget, setPinTarget] = useState(null);
  const [pinTitle, setPinTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/stories/mine"); setRows(Array.isArray(data) ? data : []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setKind("image"); setMediaUrl(null); setText(""); setBg(BG_COLORS[0]); };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMG) { toast.error(t("soc.stories_too_large")); return; }
    setUploading(true);
    try {
      const { path } = await uploadFile(file, "photo");
      setMediaUrl(fileUrl(path));
    } catch (err) { toast.error(apiError(err, t("soc.stories_upload_failed"))); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (kind === "text" ? !text.trim() : !mediaUrl) { toast.error(t("soc.stories_need_content")); return; }
    setSaving(true);
    try {
      await api.post("/stories", kind === "text"
        ? { media_kind: "text", text: text.trim(), background_color: bg, duration_ms: 5000 }
        : { media_kind: "image", media_base64: mediaUrl, duration_ms: 5000 });
      toast.success(t("soc.stories_created"));
      setOpen(false); resetForm(); await load();
    } catch (err) { toast.error(apiError(err, t("soc.stories_create_failed"))); }
    finally { setSaving(false); }
  };

  const applyPin = async () => {
    if (!pinTarget) return;
    setBusy(true);
    try { await api.post(`/stories/${pinTarget.id}/pin`, { title: pinTitle.trim() || undefined }); toast.success(t("soc.stories_pinned")); setPinTarget(null); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const unpin = async (s) => {
    setBusy(true);
    try { await api.post(`/stories/${s.id}/unpin`); toast.success(t("soc.stories_unpinned")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(t("soc.stories_delete_confirm"))) return;
    setBusy(true);
    try { await api.delete(`/stories/${s.id}`); toast.success(t("soc.stories_deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="soc-stories-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t("soc.stories_sub")}</p>
        <Button className="gap-1.5 rounded-full" onClick={() => { resetForm(); setOpen(true); }} data-testid="soc-story-new">
          <Plus className="h-4 w-4" /> {t("soc.stories_new")}
        </Button>
      </div>

      {rows === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><ImageIcon className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("soc.stories_empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("soc.stories_empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((s) => {
            const active = isActive(s);
            return (
              <div key={s.id} data-testid={`soc-story-row-${s.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <div className="flex h-20 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg" style={{ backgroundColor: s.background_color || "#0A0F1A" }}>
                  {s.media_kind === "text" ? (
                    <span className="line-clamp-3 px-1 text-center text-[8px] font-semibold text-white">{s.text || ""}</span>
                  ) : s.media_base64 ? (
                    <img src={s.media_base64} alt="" className="h-full w-full object-cover" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {s.is_highlight && <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-foreground"><Star className="h-2.5 w-2.5" /> {t("soc.stories_highlight_badge")}</span>}
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${active ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>{active ? t("soc.stories_status_active") : t("soc.stories_status_expired")}</span>
                  </div>
                  {s.is_highlight && s.highlight_title && <p className="mt-1 truncate text-sm font-medium text-foreground">{s.highlight_title}</p>}
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("soc.stories_views", { n: s.view_count || 0 })}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {s.is_highlight ? (
                    <Button size="sm" variant="outline" className="gap-1 rounded-full" disabled={busy} onClick={() => unpin(s)} data-testid={`soc-story-unpin-${s.id}`}>
                      <Star className="h-3.5 w-3.5 fill-current text-primary" /> {t("soc.stories_unpin")}
                    </Button>
                  ) : (
                    <Button size="sm" className="gap-1 rounded-full" disabled={busy} onClick={() => { setPinTarget(s); setPinTitle(s.highlight_title || ""); }} data-testid={`soc-story-pin-${s.id}`}>
                      <Star className="h-3.5 w-3.5" /> {t("soc.stories_pin")}
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-rose-500 hover:text-rose-600" disabled={busy} onClick={() => remove(s)} data-testid={`soc-story-delete-${s.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="soc-story-dialog">
          <DialogHeader>
            <DialogTitle>{t("soc.stories_dialog_title")}</DialogTitle>
            <DialogDescription>{t("soc.stories_ttl_hint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex rounded-full bg-secondary p-1">
              <button onClick={() => setKind("image")} data-testid="soc-story-kind-image" className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition-colors ${kind === "image" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                <ImageIcon className="h-4 w-4" /> {t("soc.stories_kind_image")}
              </button>
              <button onClick={() => setKind("text")} data-testid="soc-story-kind-text" className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-medium transition-colors ${kind === "text" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                <Type className="h-4 w-4" /> {t("soc.stories_kind_text")}
              </button>
            </div>

            {kind === "image" ? (
              <div className="space-y-2">
                <div className="flex aspect-[9/16] max-h-[320px] items-center justify-center overflow-hidden rounded-xl border border-border bg-secondary">
                  {uploading ? <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /> : mediaUrl ? <img src={mediaUrl} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-10 w-10 text-muted-foreground" />}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="soc-story-file" />
                <Button variant="outline" className="w-full gap-1.5 rounded-full" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="soc-story-pick">
                  {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> {t("soc.stories_uploading")}</> : <><UploadCloud className="h-4 w-4" /> {mediaUrl ? t("soc.stories_replace") : t("soc.stories_pick")}</>}
                </Button>
                <p className="text-center text-xs text-muted-foreground">{t("soc.stories_pick_hint")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex aspect-[9/16] max-h-[320px] items-center justify-center rounded-xl p-6" style={{ backgroundColor: bg }}>
                  <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={280} placeholder={t("soc.stories_text_ph")} data-testid="soc-story-text" className="resize-none border-0 bg-transparent text-center text-xl font-semibold text-white placeholder:text-white/50 focus-visible:ring-0" />
                </div>
                <div className="flex justify-center gap-2">
                  {BG_COLORS.map((c) => (
                    <button key={c} onClick={() => setBg(c)} data-testid={`soc-story-bg-${c}`} className={`h-7 w-7 rounded-full border-2 ${bg === c ? "border-foreground" : "border-transparent"}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
                <p className="text-right text-xs text-muted-foreground">{text.length} / 280</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button className="rounded-full" onClick={submit} disabled={saving || uploading} data-testid="soc-story-submit">{saving ? t("soc.stories_publishing") : t("soc.stories_publish")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pinTarget} onOpenChange={(o) => !o && setPinTarget(null)}>
        <DialogContent data-testid="soc-story-pin-dialog">
          <DialogHeader>
            <DialogTitle>{t("soc.stories_pin_title")}</DialogTitle>
            <DialogDescription>{t("soc.stories_pin_sub")}</DialogDescription>
          </DialogHeader>
          <Input value={pinTitle} onChange={(e) => setPinTitle(e.target.value)} maxLength={40} placeholder={t("soc.stories_pin_ph")} data-testid="soc-story-pin-title-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPinTarget(null)}>{t("common.cancel")}</Button>
            <Button className="rounded-full" onClick={applyPin} disabled={busy} data-testid="soc-story-pin-save">{t("soc.stories_pin_save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
